import { useState, useEffect } from "react";
import axios from "axios";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Briefcase,
  MapPin,
  Clock,
  Send,
  User,
  Phone,
  Mail,
  FileText,
  Stethoscope,
  FlaskConical,
  ClipboardList,
  Users,
  Calculator,
  Loader2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  stethoscope: Stethoscope,
  users: Users,
  "flask-conical": FlaskConical,
  "clipboard-list": ClipboardList,
  briefcase: Briefcase,
  calculator: Calculator,
};

export default function CareersPage() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPosition, setSelectedPosition] = useState("");
  const [selectedJobId, setSelectedJobId] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [cvFile, setCvFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    position: "",
    experience: "",
    registration: "",
    message: "",
  });

  useEffect(() => {
    // Seed jobs then fetch
    axios.post(`${API}/seed-jobs`).catch(() => {});
    axios.get(`${API}/jobs?active_only=true`).then((r) => {
      setJobs(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleApply = (job) => {
    setSelectedPosition(job.title);
    setSelectedJobId(job.id);
    setForm({ ...form, position: job.title });
    setShowForm(true);
    setTimeout(() => {
      document.getElementById("application-form")?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);

    let cvUrl = "";
    if (cvFile) {
      try {
        const formData = new FormData();
        formData.append("file", cvFile);
        const uploadRes = await axios.post(`${API}/upload-cv`, formData, { headers: { "Content-Type": "multipart/form-data" } });
        cvUrl = uploadRes.data.url;
        toast.success("CV uploaded successfully");
      } catch {
        toast.error("CV upload failed, but your application will still be submitted");
      }
    }

    try {
      await axios.post(`${API}/career-applications`, {
        job_id: selectedJobId,
        job_title: selectedPosition,
        applicant_name: form.name,
        email: form.email,
        phone: form.phone,
        experience: form.experience,
        registration: form.registration,
        message: form.message,
        cv_url: cvUrl,
      });
      toast.success("Application submitted successfully! We will review and get back to you.");
      setForm({ name: "", email: "", phone: "", position: "", experience: "", registration: "", message: "" });
      setCvFile(null);
      setShowForm(false);
    } catch {
      toast.error("Failed to submit application. Please try again.");
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-stone-50 pt-24 pb-16 px-4" data-testid="careers-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="bg-green-100 text-green-800 border-0 mb-4 px-4 py-1.5 rounded-full text-sm font-medium">
            Join Our Team
          </Badge>
          <h1 className="font-[Outfit] text-3xl sm:text-4xl lg:text-5xl font-bold text-stone-900 tracking-tight mb-4">
            Careers at IV Medics
          </h1>
          <p className="text-base md:text-lg text-stone-500 max-w-2xl mx-auto">
            Be part of a growing healthcare team making a difference in Ibadan. We're looking for passionate professionals to join us.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mt-6">
            <div className="flex items-center gap-2 bg-white border border-stone-100 rounded-full px-4 py-2 text-sm text-stone-600 shadow-sm">
              <MapPin className="w-4 h-4 text-green-700" />
              Bodija, Ibadan
            </div>
            <div className="flex items-center gap-2 bg-white border border-stone-100 rounded-full px-4 py-2 text-sm text-stone-600 shadow-sm">
              <Clock className="w-4 h-4 text-green-700" />
              {jobs.length} Position{jobs.length !== 1 ? "s" : ""} Open
            </div>
            <div className="flex items-center gap-2 bg-white border border-stone-100 rounded-full px-4 py-2 text-sm text-stone-600 shadow-sm">
              <Mail className="w-4 h-4 text-green-700" />
              info@ivmedicsclinic.com
            </div>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 text-green-700 animate-spin mx-auto mb-3" />
            <p className="text-stone-400">Loading positions...</p>
          </div>
        )}

        {/* Open Positions */}
        <div className="space-y-4 mb-12">
          {jobs.map((pos) => {
            const PosIcon = iconMap[pos.icon] || Briefcase;
            return (
              <div
                key={pos.id}
                data-testid={`career-${pos.id}`}
                className="bg-white rounded-2xl border border-stone-100 p-6 sm:p-8 transition-all hover:border-green-200 hover:shadow-md"
              >
                <div className="flex flex-col sm:flex-row sm:items-start gap-5">
                  <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <PosIcon className="w-6 h-6 text-green-700" />
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      <h3 className="font-[Outfit] text-lg font-bold text-stone-900">{pos.title}</h3>
                      <Badge className="bg-green-100 text-green-800 border-0 rounded-full text-xs px-3">{pos.type}</Badge>
                    </div>
                    <p className="text-sm text-stone-600 mb-4">{pos.description}</p>
                    {pos.requirements && pos.requirements.length > 0 && (
                      <div className="mb-4">
                        <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">Requirements</p>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                          {pos.requirements.map((req, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-stone-600">
                              <span className="w-1.5 h-1.5 bg-green-600 rounded-full mt-1.5 flex-shrink-0" />
                              {req}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <Button
                      data-testid={`apply-${pos.id}`}
                      onClick={() => handleApply(pos)}
                      className="bg-green-700 hover:bg-green-800 text-white rounded-full px-6 gap-2"
                    >
                      Apply Now <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Application Form */}
        {showForm && (
          <div id="application-form" className="bg-white rounded-3xl border border-stone-100 p-6 sm:p-10 shadow-sm mb-12" data-testid="career-application-form">
            <h2 className="font-[Outfit] text-2xl font-bold text-stone-900 mb-2">
              Apply for: {selectedPosition}
            </h2>
            <p className="text-stone-500 text-sm mb-8">
              Fill in your details below. Your application will be submitted directly to our recruitment team.
            </p>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <Label className="text-stone-700 text-sm font-medium mb-2 flex items-center gap-2">
                    <User className="w-4 h-4" /> Full Name *
                  </Label>
                  <Input
                    data-testid="career-name-input"
                    placeholder="Your full name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="bg-stone-50 rounded-xl border-stone-200 h-12"
                  />
                </div>
                <div>
                  <Label className="text-stone-700 text-sm font-medium mb-2 flex items-center gap-2">
                    <Mail className="w-4 h-4" /> Email Address *
                  </Label>
                  <Input
                    data-testid="career-email-input"
                    type="email"
                    placeholder="your@email.com"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="bg-stone-50 rounded-xl border-stone-200 h-12"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <Label className="text-stone-700 text-sm font-medium mb-2 flex items-center gap-2">
                    <Phone className="w-4 h-4" /> Phone Number *
                  </Label>
                  <Input
                    data-testid="career-phone-input"
                    placeholder="08012345678"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="bg-stone-50 rounded-xl border-stone-200 h-12"
                  />
                </div>
                <div>
                  <Label className="text-stone-700 text-sm font-medium mb-2 flex items-center gap-2">
                    <Briefcase className="w-4 h-4" /> Position
                  </Label>
                  <Select value={form.position} onValueChange={(v) => {
                    setForm({ ...form, position: v });
                    const job = jobs.find((j) => j.title === v);
                    if (job) { setSelectedJobId(job.id); setSelectedPosition(job.title); }
                  }}>
                    <SelectTrigger className="bg-stone-50 rounded-xl border-stone-200 h-12" data-testid="career-position-select">
                      <SelectValue placeholder="Select position" />
                    </SelectTrigger>
                    <SelectContent>
                      {jobs.map((pos) => (
                        <SelectItem key={pos.id} value={pos.title}>{pos.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-stone-700 text-sm font-medium mb-2 flex items-center gap-2">
                  <Briefcase className="w-4 h-4" /> Experience in Related Field *
                </Label>
                <Textarea
                  data-testid="career-experience-input"
                  placeholder="e.g. 3 years as a registered nurse at XYZ Hospital, 2 years in a diagnostic lab..."
                  value={form.experience}
                  onChange={(e) => setForm({ ...form, experience: e.target.value })}
                  className="bg-stone-50 rounded-xl border-stone-200 min-h-[100px]"
                />
              </div>
              <div>
                <Label className="text-stone-700 text-sm font-medium mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4" /> Professional Registration / Licence
                </Label>
                <Input
                  data-testid="career-registration-input"
                  placeholder="e.g. MDCN Reg No. 12345, MLSCN Licence, NMCN Registration..."
                  value={form.registration}
                  onChange={(e) => setForm({ ...form, registration: e.target.value })}
                  className="bg-stone-50 rounded-xl border-stone-200 h-12"
                />
              </div>
              <div>
                <Label className="text-stone-700 text-sm font-medium mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4" /> Cover Note (Optional)
                </Label>
                <Textarea
                  data-testid="career-message-input"
                  placeholder="Tell us briefly why you'd like to join IV Medics..."
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="bg-stone-50 rounded-xl border-stone-200 min-h-[120px]"
                />
              </div>
              <div>
                <Label className="text-stone-700 text-sm font-medium mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4" /> Upload CV / Resume
                </Label>
                <div className="flex items-center gap-3">
                  <label className="flex-1 cursor-pointer">
                    <div className={`bg-stone-50 border-2 border-dashed rounded-xl p-4 text-center transition-all hover:border-green-400 ${cvFile ? "border-green-500 bg-green-50" : "border-stone-200"}`}>
                      <p className="text-sm text-stone-600">{cvFile ? cvFile.name : "Click to select PDF, DOC, or DOCX file"}</p>
                      {cvFile && <p className="text-xs text-green-600 mt-1">File selected</p>}
                    </div>
                    <input data-testid="career-cv-input" type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" className="hidden" onChange={(e) => setCvFile(e.target.files[0] || null)} />
                  </label>
                  {cvFile && (
                    <Button type="button" variant="outline" size="sm" onClick={() => setCvFile(null)} className="rounded-xl border-stone-200 text-xs">Remove</Button>
                  )}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 items-center">
                <Button
                  data-testid="career-submit-btn"
                  type="submit"
                  disabled={submitting}
                  className="bg-orange-600 hover:bg-orange-700 text-white rounded-full px-8 py-6 gap-2 text-base shadow-lg w-full sm:w-auto"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</>
                  ) : (
                    <>Submit Application <Send className="w-4 h-4" /></>
                  )}
                </Button>
                <p className="text-xs text-stone-400">
                  Your application will be reviewed by our recruitment team.
                </p>
              </div>
            </form>
          </div>
        )}

        {/* General Application Note */}
        <div className="bg-green-50 rounded-2xl border border-green-100 p-6 sm:p-8 text-center">
          <h3 className="font-[Outfit] text-lg font-semibold text-stone-900 mb-2">
            Don't see a role that fits?
          </h3>
          <p className="text-sm text-stone-600 mb-4">
            We're always looking for talented healthcare professionals. Send your CV and a brief introduction to:
          </p>
          <a
            href="mailto:info@ivmedicsclinic.com?subject=General%20Job%20Application%20-%20IV%20Medics"
            className="inline-flex items-center gap-2 bg-green-700 hover:bg-green-800 text-white rounded-full px-6 py-3 font-medium transition-all shadow-md hover:shadow-lg"
            data-testid="general-apply-btn"
          >
            <Mail className="w-4 h-4" /> info@ivmedicsclinic.com
          </a>
        </div>
      </div>
    </div>
  );
}
